import type { ValidateFunction } from 'ajv';
import { GrowthBook } from '@growthbook/growthbook';
import { prepareAjvErrors } from '../../utils';
import {
  type IConfigSource,
  type GetValue,
  type GetValueOption,
  type GetValuesAll,
} from '../loader.types';

export type GrowthBookSourceAttributes = {
  id?: number | string;
  anonymousId?: number | string;
  institutionId?: number | string;
  isFreeUser?: boolean;
  isProUser?: boolean;
  isPremiumUser?: boolean;
  isClinicUser?: boolean;
  institutionsMembershipsIds?: string[];
  roomId?: string;
  tenantId?: string;
};

export type GrowthBookSourceInitSettings = GrowthBook;

export type GrowthBookSourceUpdateSettings = GrowthBookSourceAttributes;

/**
 * Creates a configuration source from the growthbook.
 * It really does nothing, all main job will be done by `init` function.
 *
 * Example:
 * ```
 * const source = new GrowthBookSource(validators.growthbook);
 * const config = new ConfigLoader(source);
 * ```
 */
export class GrowthBookSource<
  T,
  InitSettings extends GrowthBookSourceInitSettings =
    GrowthBookSourceInitSettings,
  UpdateSettings extends GrowthBookSourceUpdateSettings =
    GrowthBookSourceUpdateSettings,
> implements IConfigSource<T, InitSettings, UpdateSettings> {
  private config?: T;

  private growthbook?: GrowthBook;

  /**
   * @param validator - The generated Ajv validator.
   */
  constructor(private readonly validator: ValidateFunction) {}

  /**
   * Initializes the growthbook config.
   * @param growthbook - The growthbook instance.
   */
  async init(growthbook: GrowthBook): Promise<void> {
    this.growthbook = growthbook;

    await this.loadGrowthBookFeatures();

    this.config = this.getConfigFromGrowthBookFeatures();
    this.validate(this.config);
  }

  /**
   * Update allows us to update the attributes of the growthbook instance.
   * @param attributes - The growthbook attributes object.
   */
  async update(attributes?: GrowthBookSourceUpdateSettings): Promise<void> {
    if (attributes) {
      const updatedAttributes = {
        ...this.getGrowthBook().getAttributes(),
        ...attributes,
      };
      this.getGrowthBook().setAttributes(updatedAttributes);
    }
    this.config = this.getConfigFromGrowthBookFeatures();
    this.validate(this.config);
  }

  getValue: GetValue<T> = (key) => {
    const value = this.config?.[key];
    if (value === undefined) {
      throw new Error(
        `[GrowthBookSource/getValue] key not found: ${key.toString()}`,
      );
    }

    return value;
  };

  getValueOptional: GetValueOption<T> = (key) => {
    return this.config?.[key];
  };

  getValuesAll: GetValuesAll<T> = () => this.config;

  private validate(config: T): void {
    // validate will modify config according to schema.
    if (!this.validator(config)) {
      const errors = prepareAjvErrors(this.validator);
      console.error(errors);
      throw new Error(
        '[GrowthBookSource/constructor] config and schema mismatch',
      );
    }
  }

  private getGrowthBook(): GrowthBook {
    if (!this.growthbook) {
      throw new Error(
        '[GrowthBookSource/getGrowthBook] source was not initialized',
      );
    }
    return this.growthbook;
  }

  private async loadGrowthBookFeatures(): Promise<void> {
    await this.getGrowthBook().loadFeatures({ skipCache: true });
  }

  private getConfigFromGrowthBookFeatures(): T {
    return Object.fromEntries(
      Object.keys(this.getGrowthBook().getFeatures()).map((featureKey) => [
        featureKey,
        this.getGrowthBook().evalFeature(featureKey).value,
      ]),
    ) as T;
  }
}
